import * as THREE from 'three';
import { AudioManager } from '../audio/AudioManager';
import { Input } from '../core/Input';
import { Loop } from '../core/Loop';
import { QUALITY_PRESETS, detectQuality, isTouchDevice, type QualityLevel, type QualitySettings } from '../core/Quality';
import { createRenderer } from '../core/Renderer';
import { CameraRig } from '../player/CameraRig';
import { Character } from '../player/Character';
import { PlayerController } from '../player/PlayerController';
import { Pipeline } from '../render/Pipeline';
import { UI } from '../ui/UI';
import { Environment } from '../world/Environment';
import { Grass } from '../world/Grass';
import { GrassCut } from '../world/GrassCut';
import { Birds, Dragonflies, GuideStream, Seeds } from '../world/Life';
import { Shrines, HAIKU, type Shrine } from '../world/Shrines';
import { SlashFx } from '../world/SlashFx';
import { Sky } from '../world/Sky';
import { Terrain } from '../world/Terrain';
import { Wind } from '../world/Wind';
import { clamp, damp, mulberry32, smoothstep } from '../world/noise';

type Phase = 'loading' | 'title' | 'playing' | 'paused';

const BASE = import.meta.env.BASE_URL;
const INTERACT_RADIUS = 3.4;

const tmpV = new THREE.Vector3();
const tmpV2 = new THREE.Vector3();
const tmpDir = new THREE.Vector2();
const lookDelta = new THREE.Vector2();
const moveInput = new THREE.Vector2();
const windVelocity = new THREE.Vector3();
const reachA = new THREE.Vector3();
const reachB = new THREE.Vector3();
/** Cut reach along the blade, measured from the inner sample point (1 = the tip). */
const CUT_REACH = 2.4;

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 12000);
  private readonly env = new Environment();
  private readonly wind: Wind;
  private readonly terrain: Terrain;
  private readonly grass: Grass;
  private readonly grassCut: GrassCut;
  private readonly slashFx: SlashFx;
  /** Recently cut 0.5 m cells, so debris and the cut sound only come from standing grass. */
  private readonly cutCells = new Map<number, number>();
  private swingHitGrass = false;
  private readonly sky: Sky;
  private readonly pipeline: Pipeline;
  private readonly loop: Loop;
  private readonly input: Input;
  private readonly audio = new AudioManager();
  private readonly ui: UI;
  private readonly character: Character;
  private readonly controller: PlayerController;
  private readonly cameraRig: CameraRig;
  private readonly shrines: Shrines;
  private readonly seeds: Seeds;
  private readonly guideStream: GuideStream;
  private readonly dragonflies: Dragonflies;
  private readonly birds: Birds;
  private readonly sunLight: THREE.DirectionalLight;
  private readonly skyEnvScene = new THREE.Scene();
  private readonly pmrem: THREE.PMREMGenerator;
  private envTarget: THREE.WebGLRenderTarget | null = null;
  private envRefreshTimer = 0;
  private quality: QualitySettings;
  private qualityLocked: boolean;
  private phase: Phase = 'loading';
  private frame = 0;
  private elapsed = 0;
  private width = 0;
  private height = 0;
  private dpr = 1;
  private readonly stats = { calls: 0, triangles: 0, fps: 60 };
  private frameTimeAvg = 16;
  private perfWindow = 0;
  private guideCooldown = 0;
  private readonly guideTarget = new THREE.Vector3();
  private ritual: { shrine: Shrine; time: number; lit: boolean } | null = null;
  private endingTimer = -1;
  private endingPlayed = false;
  private pausedForScreenshot = false;
  private readonly pendingTestStates: string[] = [];
  /** Game-time countdown to a screenshot freeze requested by a `freeze@<seconds>` test state. */
  private freezeIn = -1;
  private readonly fxRng = mulberry32(2024);
  private reducedMotion = false;
  private readonly touch = isTouchDevice();
  private exposure = 0.78;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const level = detectQuality();
    this.quality = QUALITY_PRESETS[level];
    this.qualityLocked = Boolean(localStorage.getItem('kaze.quality')) || new URLSearchParams(location.search).has('quality');
    this.renderer = createRenderer(canvas);
    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    this.input = new Input(canvas);
    this.wind = new Wind(this.env);
    this.terrain = new Terrain(this.env);
    this.shrines = new Shrines(this.terrain, this.env);
    this.grassCut = new GrassCut(this.renderer);
    this.grass = new Grass(this.env, this.terrain, this.quality.grass, this.grassCut);
    this.slashFx = new SlashFx(this.env);
    this.sky = new Sky(this.env);
    this.seeds = new Seeds(this.env, this.terrain, this.touch ? 700 : 1400);
    this.guideStream = new GuideStream(this.env, this.terrain);
    this.dragonflies = new Dragonflies(this.env, this.terrain);
    this.birds = new Birds(this.env);
    this.character = new Character(this.env.uniforms);
    this.controller = new PlayerController(this.terrain);
    this.cameraRig = new CameraRig(this.camera, this.terrain);

    this.sunLight = new THREE.DirectionalLight(0xffffff, 3);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize);
    const sc = this.sunLight.shadow.camera;
    sc.left = -4;
    sc.right = 4;
    sc.top = 4;
    sc.bottom = -4;
    sc.near = 1;
    sc.far = 60;
    this.sunLight.shadow.bias = -0.0004;
    this.sunLight.shadow.normalBias = 0.03;
    this.scene.add(this.sunLight, this.sunLight.target);

    this.scene.add(
      this.sky.mesh,
      this.terrain.group,
      this.grass.group,
      this.shrines.group,
      this.character.root,
      this.seeds.points,
      this.guideStream.points,
      this.dragonflies.group,
      this.birds.mesh,
      this.slashFx.group,
    );
    const envSky = new THREE.Mesh(this.sky.mesh.geometry, this.sky.mesh.material);
    this.skyEnvScene.add(envSky);

    this.pipeline = new Pipeline(this.renderer, this.scene, this.camera, this.sky.sun, this.quality);
    this.ui = new UI(
      document.querySelector<HTMLElement>('#ui-root') ?? document.body,
      this.input,
      this.audio,
      {
        onBegin: () => this.begin(),
        onResume: () => this.resume(),
        onQuality: (lvl) => this.setQuality(lvl, true),
      },
      this.touch,
      level,
    );
    this.input.onPointerLockChange = (locked) => {
      if (!locked && this.phase === 'playing' && !this.touch) this.pause();
    };
    canvas.addEventListener('click', () => {
      if (this.phase === 'playing' && !this.input.pointerLocked) this.input.requestPointerLock();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.phase === 'playing') this.pause();
    });

    const params = new URLSearchParams(location.search);
    const spawnYaw = Math.atan2(this.env.sunDirection.x, this.env.sunDirection.z) + 0.12;
    this.controller.place(0, 0, spawnYaw);
    this.cameraRig.snap(this.controller.position, spawnYaw);
    this.cameraRig.pitch = -0.1;
    this.env.setDayProgress(Number(params.get('day') ?? 0), true);

    this.loop = new Loop(
      (dt) => this.update(dt),
      () => this.render(),
    );
    this.resize();
    window.addEventListener('resize', this.resize);
    this.refreshEnvironmentMap();
    this.installTestHooks();
    void this.loadAssets(params.has('autoplay'));
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    window.removeEventListener('resize', this.resize);
    this.input.dispose();
    this.pipeline.dispose();
    this.renderer.dispose();
    void this.audio.ctx.close();
  }

  private async loadAssets(autoplay: boolean): Promise<void> {
    let modelDone = 0;
    let audioFraction = 0;
    const progress = () => this.ui.setLoading(modelDone * 0.55 + audioFraction * 0.45);
    await Promise.all([
      this.character.load(`${BASE}models/Xbot.glb`).then(() => {
        modelDone = 1;
        progress();
      }),
      this.audio.load(BASE, (f) => {
        audioFraction = f;
        progress();
      }),
    ]);
    for (const mesh of this.character.clothMeshes) this.scene.add(mesh);
    this.syncCharacter(0);
    this.character.resetCloth();
    // Touch has one sword button, so the blade goes back on its own after a quiet spell.
    if (this.touch) this.character.sword.autoSheathe = 8;
    this.dragonflies.reset(this.controller.position);
    this.renderer.compile(this.scene, this.camera);
    this.phase = 'title';
    this.ui.showTitle();
    if (autoplay) this.begin();
    for (const name of this.pendingTestStates.splice(0)) this.applyTestState(name);
  }

  private begin(): void {
    if (this.phase !== 'title') return;
    void this.audio.unlock();
    this.phase = 'playing';
    this.input.enabled = true;
    this.ui.startPlaying();
    this.input.requestPointerLock();
    this.ui.showToast('Follow the wind to the six roadside lanterns', 6);
  }

  private pause(): void {
    if (this.phase !== 'playing') return;
    this.phase = 'paused';
    this.input.enabled = false;
    this.input.exitPointerLock();
    this.ui.setPaused(true);
    this.audio.setDucked(true);
  }

  private resume(): void {
    if (this.phase !== 'paused') return;
    this.phase = 'playing';
    this.input.enabled = true;
    this.input.clearActions();
    this.ui.setPaused(false);
    this.audio.setDucked(false);
    this.input.requestPointerLock();
  }

  private setQuality(level: QualityLevel, persist: boolean): void {
    this.quality = QUALITY_PRESETS[level];
    if (persist) {
      localStorage.setItem('kaze.quality', level);
      this.qualityLocked = true;
    }
    this.grass.setQuality(this.quality.grass);
    this.pipeline.applyQuality(this.quality);
    this.sunLight.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize);
    this.sunLight.shadow.map?.dispose();
    this.sunLight.shadow.map = null;
    this.width = 0;
    this.resize();
    this.ui.setQuality(level);
  }

  private readonly resize = () => {
    const width = Math.max(1, Math.floor(this.canvas.clientWidth || window.innerWidth));
    const height = Math.max(1, Math.floor(this.canvas.clientHeight || window.innerHeight));
    const dpr = Math.min(window.devicePixelRatio || 1, this.quality.dprCap);
    if (width === this.width && height === this.height && dpr === this.dpr) return;
    this.width = width;
    this.height = height;
    this.dpr = dpr;
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(width, height, false);
    this.pipeline.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  };

  private refreshEnvironmentMap(): void {
    const target = this.pmrem.fromScene(this.skyEnvScene, 0, 0.1, 500);
    this.scene.environment = target.texture;
    this.scene.environmentIntensity = 0.85;
    this.envTarget?.dispose();
    this.envTarget = target;
  }

  private update(rawDt: number): void {
    const info = this.renderer.info;
    this.stats.calls = info.render.calls;
    this.stats.triangles = info.render.triangles;
    info.reset();
    this.frame++;
    this.trackPerformance(rawDt);
    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }
    this.step(rawDt);
  }

  private step(rawDt: number): void {
    const dt = Math.min(rawDt, 1 / 20);
    if (this.freezeIn >= 0) {
      this.freezeIn -= dt;
      if (this.freezeIn < 0) this.pausedForScreenshot = true;
    }
    const worldDt = this.phase === 'paused' ? dt * 0.35 : dt;
    this.elapsed += worldDt;
    this.env.uniforms.uTime.value = this.reducedMotion ? 0 : this.elapsed;
    if (this.env.update(worldDt)) {
      this.envRefreshTimer -= worldDt;
      if (this.envRefreshTimer <= 0) {
        this.envRefreshTimer = 1.2;
        this.refreshEnvironmentMap();
      }
    }

    this.input.poll(dt);
    if (this.phase === 'playing') this.updatePlaying(dt);
    else if (this.phase === 'paused' && this.input.consume('pause')) this.resume();
    else this.input.clearActions();

    this.wind.update(worldDt, this.controller.position);
    this.syncCharacter(this.phase === 'paused' ? 0 : dt);

    const playerSpeed = this.controller.horizontalSpeed;
    this.cameraRig.cinematic = damp(this.cameraRig.cinematic, this.phase === 'title' || this.phase === 'loading' ? 1 : 0, 1.4, dt);
    this.input.consumeLook(lookDelta, dt);
    this.cameraRig.update(
      this.phase === 'paused' ? 0 : dt,
      this.controller.position,
      this.controller.yaw,
      playerSpeed,
      this.controller.sprint,
      this.phase === 'playing' ? lookDelta : lookDelta.set(0, 0),
      this.input.lookActivity,
      this.characterPhase(),
    );

    this.updateExposure(dt);
    this.sky.update(this.camera);
    this.sunLight.position.copy(this.controller.position).addScaledVector(this.env.sunDirection, 30);
    this.sunLight.target.position.copy(this.controller.position);
    this.sunLight.target.updateMatrixWorld();
    this.sunLight.color.copy(this.env.uniforms.uSunColor.value).multiplyScalar(1 / Math.max(this.env.uniforms.uSunColor.value.r, 1e-3));
    this.sunLight.intensity = this.env.sunLightIntensity;

    this.updateSwordFx(worldDt);
    this.grass.update(worldDt, this.camera, this.controller.position, playerSpeed);
    const gust = this.wind.gustAtPlayer;
    this.seeds.update(worldDt, this.wind.direction, this.wind.strength, gust, this.height * this.dpr);
    this.dragonflies.update(worldDt, this.controller.position, this.wind.direction, gust);
    this.birds.update(worldDt, this.controller.position);
    const guide = this.wind.guideAmount;
    tmpDir.set(this.guideTarget.x - this.controller.position.x, this.guideTarget.z - this.controller.position.z);
    this.guideStream.update(this.controller.position, tmpDir, guide, clamp(tmpDir.length() - 2, 8, 46), this.height * this.dpr);

    for (const shrine of this.shrines.list) {
      const g = this.wind.gustAt(shrine.position.x, shrine.position.z);
      windVelocity.set(this.wind.direction.x, 0, this.wind.direction.y).multiplyScalar((1.4 + g * 5) * this.wind.strength);
      if (shrine.update(worldDt, windVelocity, g)) this.audio.playAt('chime', shrine.chimePosition, { volume: 0.55, ref: 7, rate: 0.96 + Math.random() * 0.08 });
    }

    this.updateAudio(dt);
    this.ui.update(dt, playerSpeed > 0.5);
    this.publishDiagnostics();
  }

  private updatePlaying(dt: number): void {
    const input = this.input;
    if (input.consume('pause')) {
      this.pause();
      return;
    }
    if (input.consume('hideUi')) this.ui.toggleHud();
    if (input.consume('walkToggle')) {
      this.controller.walkMode = !this.controller.walkMode;
      this.ui.showToast(this.controller.walkMode ? 'Walking' : 'Running', 1.6);
    }
    input.readMove(moveInput);
    const locked = this.ritual !== null;
    const sword = this.character.sword;
    if (this.character.loaded && !locked) {
      if (input.consume('equip')) sword.toggle();
      if (input.consume('slash') && !this.character.flipping) sword.press();
    }
    const slashLocked = this.character.loaded && sword.locksMovement;
    this.controller.update(
      dt,
      moveInput,
      this.cameraRig.forwardYaw,
      input.sprintHeld(),
      input.consume('jump'),
      this.shrines.colliders,
      locked,
      slashLocked,
      this.character.loaded && sword.canAim,
      input.crouchHeld(),
    );
    if (this.controller.slideStarted) {
      this.audio.play('flipCloth', { volume: 0.6, rate: 0.72 + this.fxRng() * 0.08 });
      this.audio.play('jump', { volume: 0.3, rate: 0.78 });
      this.cameraRig.kick(0.08, 4);
    }
    const low = this.controller.slideTime >= 0 ? 0.55 : this.controller.crouching ? 0.42 : 0;
    this.cameraRig.lower = damp(this.cameraRig.lower, low, 7, dt);
    this.cameraRig.shoulder = damp(this.cameraRig.shoulder, this.character.loaded && sword.drawn ? 1 : 0, 3, dt);
    if (this.controller.jumped) this.audio.play('jump', { volume: 0.4 });
    if (this.controller.doubleJumped) this.onDoubleJump();
    if (this.controller.landedImpact > 2) this.audio.play('land', { volume: clamp(this.controller.landedImpact / 7, 0.3, 0.9) });

    this.guideCooldown -= dt;
    if (input.consume('wind') && this.guideCooldown <= 0) this.summonGuidingWind();

    // Shrine proximity and ritual.
    const near = this.shrines.nearestDormant(this.controller.position);
    const inRange = near && near.position.distanceTo(this.controller.position) < INTERACT_RADIUS && !this.ritual;
    this.ui.setPrompt(inRange ? (this.touch ? 'Pay respects' : '<kbd>E</kbd>Pay respects') : null);
    if (inRange && near && input.consume('interact')) this.startRitual(near);
    else input.consume('interact');
    if (this.ritual) this.updateRitual(dt);
    if (!this.endingPlayed && !this.ritual && this.shrines.litCount === this.shrines.list.length) {
      this.endingPlayed = true;
      this.endingTimer = 0;
    }
    if (this.endingTimer >= 0) this.updateEnding(dt);

    this.ui.setEdgeHint(this.controller.edgeFactor > 0.35);
  }

  private onDoubleJump(): void {
    this.character.flip();
    const p = this.controller.position;
    this.slashFx.windRing(tmpV.copy(p).setY(p.y + 0.05), 2.6, 1, 0.5);
    this.grass.shockwave(p.x, p.z, 1.5, 7, 1.7);
    this.cameraRig.kick(0.12, 3);
    // doubleJump bursts at 0.24 s; the cloth snap peaks at 0.04 s.
    this.audio.play('doubleJump', { volume: 0.7, offset: 0.2, rate: 0.97 + this.fxRng() * 0.06 });
    this.audio.play('flipCloth', { volume: 0.55, rate: 0.92 + this.fxRng() * 0.16, delay: 0.04 });
  }

  /** Sword sounds are scheduled so each clip's peak lands on the animation beat it belongs to. */
  private playSwordEvents(): void {
    const events = this.character.sword.events;
    for (const e of events) {
      const vary = 0.96 + this.fxRng() * 0.08;
      if (e.type === 'draw') {
        // The ring peaks 0.82 s in; land it as the kissaki clears the koiguchi.
        this.audio.play('swordDraw', { volume: 0.42, offset: Math.max(0, 0.82 - e.clearIn) });
      } else if (e.type === 'swing') {
        const peak = e.hit === 1 ? 0.38 : e.hit === 2 ? 0.08 : 0.4;
        const name = e.hit === 1 ? 'slash1' : e.hit === 2 ? 'slash2' : 'slash3';
        const volume = e.hit === 2 ? 0.95 : e.hit === 3 ? 0.75 : 0.7;
        const lead = e.contactIn - peak;
        this.audio.play(name, { volume, rate: vary, delay: Math.max(0, lead), offset: Math.max(0, -lead) });
        this.swingHitGrass = false;
      } else if (e.type === 'contact') {
        this.cameraRig.kick(e.hit === 3 ? 0.3 : 0.18);
        if (e.hit === 3) {
          const p = this.controller.position;
          this.grass.shockwave(p.x, p.z, 0.9, 5, 1.3);
        }
      } else if (e.type === 'sheathe') {
        // The tsuba click peaks 0.98 s into the clip.
        const lead = e.seatIn - 0.98;
        this.audio.play('sheathe', { volume: 0.6, delay: Math.max(0, lead), offset: Math.max(0, -lead) });
      }
    }
    events.length = 0;
  }

  /** Trail, grass cutting and debris from the blade's sweep this frame. */
  private updateSwordFx(dt: number): void {
    const sword = this.character.sword;
    if (!this.character.loaded) return;
    const power = sword.swinging ? smoothstep(5, 13, sword.tipSpeed) : 0;
    this.slashFx.updateTrail(this.elapsed, sword.base, sword.tip, power);
    if (sword.cutting) {
      const ground = (x: number, z: number) => this.terrain.heightAt(x, z);
      // The cut reaches past the edge: the wind off the blade shears the field in a readable arc.
      reachA.lerpVectors(sword.prevBase, sword.prevTip, CUT_REACH);
      reachB.lerpVectors(sword.base, sword.tip, CUT_REACH);
      this.grassCut.stroke(sword.prevBase, reachA, sword.base, reachB, ground);
      tmpV2.subVectors(sword.tip, sword.prevTip).divideScalar(Math.max(dt, 1e-3));
      for (const k of [0.4, 0.9, 1.5, 2.1]) {
        tmpV.lerpVectors(sword.base, sword.tip, k);
        const h = tmpV.y - this.terrain.heightAt(tmpV.x, tmpV.z);
        if (h < 0.08 || h > 0.95 || this.inClearing(tmpV.x, tmpV.z)) continue;
        const cell = Math.floor(tmpV.x * 2) * 73856093 + Math.floor(tmpV.z * 2) * 19349663;
        const last = this.cutCells.get(cell);
        if (last !== undefined && this.elapsed - last < 18) continue;
        this.cutCells.set(cell, this.elapsed);
        this.slashFx.spawnDebris(tmpV, tmpV2, 3);
        if (!this.swingHitGrass) {
          this.swingHitGrass = true;
          const second = this.fxRng() < 0.5;
          this.audio.play(second ? 'grassCut2' : 'grassCut1', { volume: 0.8, rate: 0.94 + this.fxRng() * 0.12 });
        }
      }
      if (this.cutCells.size > 4000) for (const [c, t] of this.cutCells) if (this.elapsed - t > 18) this.cutCells.delete(c);
    }
    // A slide throws up chaff from under the lead foot.
    if (this.controller.slideTime >= 0 && this.controller.slideTime < 0.7 && !this.inClearing(this.controller.position.x, this.controller.position.z)) {
      const p = this.controller.position;
      const v = this.controller.velocity;
      tmpV.set(p.x + v.x * 0.08, p.y + 0.15, p.z + v.z * 0.08);
      tmpV2.set(v.x * 0.6, 0, v.z * 0.6);
      if (this.fxRng() < 0.6) this.slashFx.spawnDebris(tmpV, tmpV2, 1);
    }
    this.grassCut.update(dt, this.controller.position);
    windVelocity.set(this.wind.direction.x, 0, this.wind.direction.y).multiplyScalar(1 + this.wind.gustAtPlayer * 3);
    this.slashFx.update(dt, windVelocity, (x, z) => this.terrain.heightAt(x, z), this.height * this.dpr);
  }

  private inClearing(x: number, z: number): boolean {
    for (const c of this.terrain.clearings) if (c.z > 0 && Math.hypot(x - c.x, z - c.y) < c.z * 0.9) return true;
    return false;
  }

  private summonGuidingWind(): void {
    const target = this.shrines.nearestDormant(this.controller.position);
    this.guideCooldown = 4;
    if (!target) {
      this.ui.showToast('Every lantern glows. Wander where the wind goes.');
      return;
    }
    this.guideTarget.copy(target.position);
    tmpDir.set(target.position.x - this.controller.position.x, target.position.z - this.controller.position.z);
    this.wind.guide(tmpDir, 7);
    this.audio.play('guide', { volume: 0.55, group: 'ambience' });
    this.ui.showCounter(this.shrines.litCount, this.shrines.list.length, 4);
  }

  private startRitual(shrine: Shrine): void {
    this.ritual = { shrine, time: 0, lit: false };
    this.character.sword.sheathe();
    this.ui.setPrompt(null);
    this.character.bow();
    this.audio.play('uiConfirm', { group: 'ui', volume: 0.35 });
  }

  private updateRitual(dt: number): void {
    const r = this.ritual;
    if (!r) return;
    r.time += dt;
    // Face the statue while bowing.
    const toShrine = Math.atan2(r.shrine.position.x - this.controller.position.x, r.shrine.position.z - this.controller.position.z);
    let delta = toShrine - this.controller.yaw;
    delta = Math.atan2(Math.sin(delta), Math.cos(delta));
    this.controller.yaw += delta * Math.min(1, dt * 5);
    this.cameraRig.focusYaw = toShrine + 0.55;
    if (!r.lit && r.time > 1.3) {
      r.lit = true;
      r.shrine.ignite();
      this.audio.playAt('bell', r.shrine.position.clone().setY(r.shrine.position.y + 1), { volume: 0.9, ref: 8 });
      const lit = this.shrines.litCount;
      this.ui.showHaiku(HAIKU[(lit - 1) % HAIKU.length]);
      this.ui.showCounter(lit, this.shrines.list.length, 8);
      this.env.setDayProgress(lit / this.shrines.list.length);
    }
    if (r.time > 3.3) {
      this.ritual = null;
      this.cameraRig.focusYaw = null;
    }
  }

  private updateEnding(dt: number): void {
    const before = this.endingTimer;
    this.endingTimer += dt;
    if (before < 9 && this.endingTimer >= 9) this.ui.showToast('The wind rests. Thank you for walking with it.', 9);
    if (this.endingTimer > 20) this.endingTimer = -1;
  }

  private characterPhase(): number {
    return (this.elapsed * this.controller.horizontalSpeed) / 2.3;
  }

  private syncCharacter(dt: number): void {
    const c = this.controller;
    this.character.root.position.copy(c.position);
    this.character.root.rotation.y = c.yaw;
    const g = this.wind.gustAtPlayer;
    windVelocity
      .set(this.wind.direction.x, 0, this.wind.direction.y)
      .multiplyScalar((1.6 + g * 5.5) * this.wind.strength + this.wind.guideAmount * 3);
    this.character.update(
      dt,
      {
        speed: c.horizontalSpeed,
        grounded: c.grounded,
        verticalVelocity: c.velocity.y,
        turnRate: c.turnRate,
        sprint: c.sprint,
        crouch: c.crouching,
        slide: c.slideTime >= 0,
      },
      windVelocity,
    );
    for (const step of this.character.footsteps) this.audio.footstep(step.intensity);
    this.character.footsteps.length = 0;
    if (this.character.loaded) this.playSwordEvents();

    // Shadow proxies for the grass and terrain shaders.
    const u = this.env.uniforms;
    this.character.bonePosition('Hips', tmpV);
    this.character.bonePosition('Neck', tmpV2);
    u.uCapsuleA.value.set(tmpV.x, c.position.y + 0.25, tmpV.z, 0.2);
    u.uCapsuleB.value.set(tmpV2.x, tmpV2.y, tmpV2.z, this.character.loaded ? 0.78 : 0);
    this.character.hatPosition(tmpV);
    u.uHatSphere.value.set(tmpV.x, tmpV.y + 0.05, tmpV.z, 0.3);
  }

  /** Eye adaptation: backlit views keep the golden glow, front-lit views stop blowing out. */
  private updateExposure(dt: number): void {
    this.camera.getWorldDirection(tmpV);
    const sun = this.env.sunDirection;
    const flat = Math.hypot(tmpV.x, tmpV.z) || 1;
    const sunFlat = Math.hypot(sun.x, sun.z) || 1;
    const toward = (tmpV.x * sun.x + tmpV.z * sun.z) / (flat * sunFlat);
    const lookUp = clamp(tmpV.y * 2, 0, 1);
    const target = 0.6 + smoothstep(-0.5, 0.85, toward) * 0.18 - lookUp * 0.05 + (1 - this.env.displayed) * 0.0 + this.env.displayed * 0.06;
    this.exposure = damp(this.exposure, target, 1.1, dt);
    this.renderer.toneMappingExposure = this.exposure;
  }

  private updateAudio(dt: number): void {
    const right = tmpV.set(1, 0, 0).applyQuaternion(this.camera.quaternion);
    const windPanX = -(this.wind.direction.x * right.x + this.wind.direction.y * right.z);
    let inGrass = 1;
    for (const s of this.shrines.list) inGrass = Math.min(inGrass, smoothstep(2.2, 3.6, s.position.distanceTo(this.controller.position)));
    this.audio.updateAmbience(
      dt,
      {
        gust: this.wind.gustAtPlayer,
        windStrength: this.wind.strength,
        windPanX,
        speed: this.controller.horizontalSpeed,
        sprint: this.controller.sprint,
        guide: this.wind.guideAmount,
        grounded: this.controller.grounded,
        inGrass,
      },
      this.camera,
    );
  }

  private trackPerformance(rawDt: number): void {
    const ms = rawDt * 1000;
    this.frameTimeAvg += (ms - this.frameTimeAvg) * 0.05;
    this.stats.fps = 1000 / Math.max(this.frameTimeAvg, 1);
    if (this.qualityLocked || this.phase !== 'playing') return;
    this.perfWindow += rawDt;
    if (this.perfWindow > 6) {
      this.perfWindow = 0;
      if (this.frameTimeAvg > 28 && this.quality.level !== 'low') {
        this.setQuality(this.quality.level === 'high' ? 'medium' : 'low', false);
      }
    }
  }

  private render(): void {
    this.pipeline.render(1 / 60);
  }

  private applyTestState(name: string): void {
    if (name === 'active-play' || name === 'playing') {
      if (this.phase === 'title') this.begin();
    } else if (name === 'title') {
      // Title is the natural post-load state.
    } else if (name === 'near-shrine') {
      if (this.phase === 'title') this.begin();
      const s = this.shrines.list[0];
      this.controller.place(s.position.x + s.front.x * 2.7, s.position.z + s.front.z * 2.7, Math.atan2(-s.front.x, -s.front.z));
      this.cameraRig.snap(this.controller.position, this.controller.yaw);
      this.character.resetCloth();
    } else if (name === 'light-one') {
      const s = this.shrines.nearestDormant(this.controller.position);
      if (s) this.startRitual(s);
    } else if (name === 'sunset') {
      this.env.setDayProgress(1, true);
      this.refreshEnvironmentMap();
    } else if (name === 'guide') {
      this.summonGuidingWind();
    } else if (name === 'pause') {
      this.pause();
    } else if (name === 'slash') {
      if (this.phase === 'title') this.begin();
      this.character.sword.press();
    } else if (name === 'equip') {
      if (this.phase === 'title') this.begin();
      this.character.sword.toggle();
    } else if (name === 'jump') {
      if (this.phase === 'title') this.begin();
      this.input.press('jump');
    } else if (name.startsWith('freeze@')) {
      // Freeze the simulation after this much game time, for mid-action screenshots.
      this.freezeIn = Number(name.slice(7)) || 0;
    } else {
      console.warn(`Unknown test state: ${name}`);
    }
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: () => {},
      setState: (name: string) => {
        // 'a+b' applies several states in order, e.g. 'near-shrine+light-one'.
        const names = name.split('+');
        if (this.phase === 'loading') this.pendingTestStates.push(...names);
        else for (const part of names) this.applyTestState(part);
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
      },
      hideDebugUi: () => {},
      debugCamera: (yawOffset: number, pitch: number, distance: number) => {
        this.cameraRig.debugView = [yawOffset, pitch, distance];
      },
      advance: (seconds: number) => {
        // Deterministic stepping while frozen: 60 Hz sub-steps.
        this.pausedForScreenshot = true;
        let left = seconds;
        while (left > 1e-6) {
          const dt = Math.min(left, 1 / 60);
          this.step(dt);
          left -= dt;
        }
      },
    } as ThreeGameTestHooks;
  }

  private publishDiagnostics(): void {
    const p = this.controller.position;
    const target = this.shrines.nearestDormant(p);
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      score: this.shrines.litCount,
      targetScore: this.shrines.list.length,
      complete: this.shrines.litCount === this.shrines.list.length,
      player: { position: { x: p.x, y: p.y, z: p.z }, speed: this.controller.horizontalSpeed },
      renderer: {
        calls: this.stats.calls,
        triangles: this.stats.triangles,
        geometries: this.renderer.info.memory.geometries,
        textures: this.renderer.info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: this.dpr,
      },
      extra: {
        phase: this.phase,
        fps: Math.round(this.stats.fps),
        quality: this.quality.level,
        blades: this.grass.visibleBlades,
        gust: Number(this.wind.gustAtPlayer.toFixed(3)),
        guide: Number(this.wind.guideAmount.toFixed(3)),
        audio: this.audio.running ? 'running' : 'suspended',
        day: Number(this.env.displayed.toFixed(3)),
        grounded: this.controller.grounded,
        jumps: this.controller.jumps,
        sword: this.character.loaded ? this.character.sword.state : 'loading',
        flip: this.character.flipping,
        crouch: this.controller.crouching,
        slide: this.controller.slideTime >= 0,
        cuts: this.grassCut.strokes,
        cameraYaw: this.cameraRig.forwardYaw,
        ...(target ? { targetX: target.position.x, targetZ: target.position.z } : {}),
      },
    };
  }
}
